import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import TweetPreviewModal from "@/components/TweetPreviewModal";
import {
  Sparkles, Trash2, Zap, TrendingUp, MousePointerClick, Wallet, X, Copy, ExternalLink,
  FileText, Star, Table, ChevronRight, Radio, RadioTower, KeyRound, RefreshCw, Twitter, Languages,
  Globe, Lock,
} from "lucide-react";

const COUNTRIES = ["United States", "United Kingdom", "Germany", "France", "Spain", "Netherlands", "India", "Japan", "Australia", "Canada", "Brazil", "Mexico", "Singapore", "UAE"];
const LANGUAGES = ["English", "Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Bengali", "Kannada", "Punjabi", "Malayalam", "Spanish", "French", "German", "Portuguese", "Japanese", "Arabic", "Mandarin", "Italian", "Dutch"];
const NETWORK_HINTS = ["auto-detect", "amazon", "clickbank", "shareasale", "cj", "impact", "rakuten", "awin", "generic"];

function TabPill({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-mono uppercase tracking-widest text-[11px] transition ${
        active
          ? "bg-sky-400 text-slate-950"
          : "bg-slate-900 text-neutral-400 hover:text-sky-100 border border-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

export default function AffiliateModule() {
  const [tab, setTab] = useState("connect"); // connect | accounts | studio | content | analytics
  const [accounts, setAccounts] = useState([]);
  const [content, setContent] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [topics, setTopics] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [viewContent, setViewContent] = useState(null);
  const [toast, setToast] = useState("");

  // Connect form
  const [affId, setAffId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [hint, setHint] = useState("auto-detect");
  const [country, setCountry] = useState("United States");
  const [language, setLanguage] = useState("English");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [lastConnected, setLastConnected] = useState(null);

  // Studio form
  const [topic, setTopic] = useState("");
  const [contentType, setContentType] = useState("article");
  const [products, setProducts] = useState("");
  const [includeHeroImage, setIncludeHeroImage] = useState(false);
  const [languages, setLanguages] = useState([]); // multilingual mode
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState("");

  // Network sync modal
  const [syncModal, setSyncModal] = useState(null); // { account, creds }
  const [syncForm, setSyncForm] = useState({ access_key: "", secret_key: "", api_key: "", account_sid: "", auth_token: "", marketplace: "US" });
  const [syncSaving, setSyncSaving] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3500); };

  const loadAll = async () => {
    try {
      const [a, c, an, tp] = await Promise.all([
        api.get("/affiliate/accounts"),
        api.get("/affiliate/content"),
        api.get("/affiliate/analytics/summary"),
        api.get("/topics"),
      ]);
      setAccounts(a.data);
      setContent(c.data);
      setAnalytics(an.data);
      setTopics(tp.data);
      if (a.data.length && !selectedAccount) setSelectedAccount(a.data[0]);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnectError(""); setLastConnected(null); setConnecting(true);
    try {
      const { data } = await api.post("/affiliate/connect", {
        affiliate_id: affId,
        api_key: apiKey,
        network_hint: hint === "auto-detect" ? "" : hint,
        country,
        language,
      });
      setLastConnected(data.account);
      setSelectedAccount(data.account);
      showToast(`Detected ${data.account.network.network_name} · niche: ${data.account.niche.niche}`);
      setAffId(""); setApiKey("");
      await loadAll();
      setTab("studio");
    } catch (err) {
      setConnectError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!selectedAccount) { setGenerateError("Select an affiliate account first."); return; }
    setGenerateError(""); setGenerating(true);
    try {
      const payload = {
        account_id: selectedAccount.id,
        topic,
        content_type: contentType,
        include_hero_image: includeHeroImage,
      };
      if (contentType === "comparison") {
        payload.products = products.split(",").map((p) => p.trim()).filter(Boolean);
        if (payload.products.length < 2) throw new Error("Enter at least 2 products separated by comma.");
      }
      let data;
      if (languages.length > 0) {
        // Always include the account's base language first
        const baseLang = selectedAccount.language || "English";
        payload.languages = [baseLang, ...languages.filter((l) => l !== baseLang)];
        const resp = await api.post("/affiliate/content/generate-multilingual", payload, { timeout: 240000 });
        data = resp.data;
        showToast(`Published ${data.generated}/${data.requested} language versions`);
        await loadAll();
        setTab("content");
      } else {
        const resp = await api.post("/affiliate/content/generate", payload, { timeout: 120000 });
        data = resp.data;
        showToast(`Published: ${data.title}`);
        setViewContent(data);
        await loadAll();
        setTab("content");
        // OAuth 2.0 auto-post preview: backend queued a tweet — open the review modal
        if (data.pending_tweet) {
          setPendingTweet(data.pending_tweet);
        }
      }
      setTopic(""); setProducts("");
    } catch (err) {
      setGenerateError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm("Disconnect this account?")) return;
    await api.delete(`/affiliate/accounts/${id}`);
    if (selectedAccount?.id === id) setSelectedAccount(null);
    await loadAll();
    showToast("Account disconnected.");
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    await api.delete(`/affiliate/content/${id}`);
    await loadAll();
    showToast("Content deleted.");
  };

  const handleRefreshContent = async (id) => {
    if (!window.confirm("Rewrite this article with AI? Adds new intro, sections, and conclusion.")) return;
    showToast("Refreshing… may take 20-30s.");
    try {
      const { data } = await api.post(`/affiliate/content/${id}/refresh`);
      showToast(`Refreshed. SEO score ${data.content?.seo_score ?? "—"}`);
      await loadAll();
    } catch (err) {
      showToast("Refresh failed. Check server logs.");
    }
  };

  const handleTranslateAll = async (id) => {
    if (!window.confirm("Translate this article into every language you've previously published in?\n\nEach language takes ~30-45s. Runs in parallel.")) return;
    showToast("Translating in parallel… please wait.");
    try {
      const { data } = await api.post(`/affiliate/content/${id}/translate-to-all`, {}, { timeout: 240000 });
      if (data.requested === 0) {
        showToast(data.note || "All languages already covered.");
      } else {
        showToast(`Generated ${data.generated}/${data.requested} language versions.`);
      }
      await loadAll();
    } catch (err) {
      showToast("Translation failed. " + (formatApiErrorDetail(err.response?.data?.detail) || err.message));
    }
  };

  const togglePublic = async (article) => {
    const nextValue = !article.is_public;
    try {
      const { data } = await api.patch(`/public/articles/${article.id}/visibility`, { is_public: nextValue });
      showToast(nextValue ? `Now public at /read/${data.slug}` : "Article is now private.");
      await loadAll();
    } catch (err) {
      showToast("Toggle failed: " + (formatApiErrorDetail(err.response?.data?.detail) || err.message));
    }
  };

  const copyPublicUrl = (slug) => {
    const url = `${window.location.origin}/read/${slug}`;
    navigator.clipboard.writeText(url);
    showToast("Public URL copied.");
  };

  // Tweet modal state
  const [tweetModal, setTweetModal] = useState(null); // { content, result, loading, selected }
  const [pendingTweet, setPendingTweet] = useState(null); // OAuth 2.0 preview: { id, tweet, primary, alt_1, alt_2, hashtags, char_count, trackable_link }

  // Load any pending tweets on mount so the user sees them if they navigated away
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/social/tweet/pending");
        if (data.items?.length > 0) setPendingTweet(data.items[0]);
      } catch { /* silent */ }
    })();
  }, []);

  const openTweetModal = async (content) => {
    setTweetModal({ content, result: null, loading: true, selected: "primary" });
    try {
      const { data } = await api.post("/social/tweet/generate", { content_id: content.id });
      setTweetModal({ content, result: data, loading: false, selected: "primary" });
    } catch (e) {
      setTweetModal({ content, result: null, loading: false, selected: "primary", error: e.message });
    }
  };

  const chosenTweet = () => {
    if (!tweetModal?.result) return "";
    const map = {
      primary: tweetModal.result.primary,
      alt_1: tweetModal.result.alt_1,
      alt_2: tweetModal.result.alt_2,
    };
    // Include link + hashtags on any variant
    const link = tweetModal.result.tweet.split(" ").find((w) => w.startsWith("http")) || "";
    const hashtags = tweetModal.result.hashtags?.slice(0, 2).map((h) => `#${h.replace(/^#/, "")}`).join(" ") || "";
    const base = map[tweetModal.selected] || tweetModal.result.tweet;
    let out = base;
    if (link && !out.includes(link)) out = `${out} ${link}`;
    if (hashtags && !out.includes(hashtags.split(" ")[0])) out = `${out} ${hashtags}`;
    return out.trim().slice(0, 280);
  };

  const copyTweet = () => {
    navigator.clipboard.writeText(chosenTweet());
    showToast("Tweet copied — paste into X.");
  };

  const openInX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(chosenTweet())}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openContent = async (id) => {
    const { data } = await api.get(`/affiliate/content/${id}`);
    setViewContent(data);
  };

  const copyLink = (url) => {
    const full = url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(full);
    showToast("Trackable link copied.");
  };

  const openSyncModal = async (account) => {
    setSyncResult(null);
    try {
      const { data } = await api.get(`/network-sync/credentials/${account.id}`);
      setSyncModal({ account, creds: data });
      setSyncForm({
        access_key: "", secret_key: "", api_key: "", account_sid: "", auth_token: "",
        marketplace: data.marketplace || "US",
      });
    } catch (e) {
      setSyncModal({ account, creds: { configured: false } });
    }
  };

  const handleSaveSync = async (e) => {
    e.preventDefault();
    setSyncSaving(true);
    try {
      await api.post("/network-sync/credentials", { account_id: syncModal.account.id, ...syncForm });
      const { data: result } = await api.post(`/network-sync/sync/${syncModal.account.id}`);
      setSyncResult(result);
      showToast(result.ok ? "Real sync activated ✓" : `Sync check: ${result.error || "issue"}`);
      loadAll();
    } catch (err) {
      setSyncResult({ ok: false, error: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setSyncSaving(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      {/* Header */}
      <div className="border-b border-slate-800 pb-6">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-2">— Affiliate Studio</p>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className="font-serif text-5xl leading-none text-sky-100 tracking-tight">
              Plug in. <span className="italic text-sky-400/90">Cash out.</span>
            </h1>
            <p className="mt-3 text-neutral-400 max-w-2xl">
              Enter one credential. Our engine detects the network, picks a niche, ships the site, drops the links, and tracks every click.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border border-slate-800 rounded px-3 py-2">
              <p className="font-numeric text-lg text-sky-100">{analytics?.connected_accounts ?? 0}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Accounts</p>
            </div>
            <div className="border border-slate-800 rounded px-3 py-2">
              <p className="font-numeric text-lg text-sky-100">{analytics?.total_content ?? 0}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-neutral-500">Content</p>
            </div>
            <div className="border border-sky-500/40 rounded px-3 py-2 bg-sky-500/5">
              <p className="font-numeric text-lg text-sky-300">${(analytics?.total_commission ?? 0).toFixed(2)}</p>
              <p className="font-mono text-[9px] uppercase tracking-widest text-sky-300/70">Commission</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-3">
        <TabPill active={tab === "connect"} onClick={() => setTab("connect")} testid="tab-connect">01 · Connect</TabPill>
        <TabPill active={tab === "accounts"} onClick={() => setTab("accounts")} testid="tab-accounts">02 · Accounts ({accounts.length})</TabPill>
        <TabPill active={tab === "studio"} onClick={() => setTab("studio")} testid="tab-studio">03 · Generate</TabPill>
        <TabPill active={tab === "content"} onClick={() => setTab("content")} testid="tab-content">04 · Content ({content.length})</TabPill>
        <TabPill active={tab === "analytics"} onClick={() => setTab("analytics")} testid="tab-analytics">05 · Analytics</TabPill>
      </div>

      {/* Toast */}
      {toast && (
        <div data-testid="toast" className="fixed bottom-6 right-6 z-50 bg-sky-400 text-slate-950 px-5 py-3 rounded-md font-medium shadow-2xl">
          {toast}
        </div>
      )}

      {/* CONNECT TAB */}
      {tab === "connect" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <form onSubmit={handleConnect} className="lg:col-span-3 border border-slate-800 rounded-xl p-8 bg-slate-900/30 space-y-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Step 01</p>
              <h2 className="font-serif text-2xl text-sky-100 mt-1">Connect your affiliate credentials</h2>
              <p className="text-sm text-neutral-500 mt-2">The AI will detect the network automatically — no manual configuration.</p>
            </div>

            {connectError && (
              <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{connectError}</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Affiliate ID / Tag *</label>
                <input
                  data-testid="input-affiliate-id"
                  required
                  value={affId}
                  onChange={(e) => setAffId(e.target.value)}
                  placeholder="myshop-20, 45892, iradX7YZ…"
                  className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none font-mono transition"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">API Key (optional)</label>
                <input
                  data-testid="input-api-key"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="For advanced networks"
                  className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none transition"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Network Hint</label>
                <select
                  data-testid="select-hint"
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
                >
                  {NETWORK_HINTS.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Country</label>
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
                >
                  {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Language</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
                >
                  {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                </select>
              </div>
            </div>

            <button
              data-testid="btn-connect-affiliate"
              type="submit"
              disabled={connecting}
              className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium rounded-md transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  Detecting network & picking niche…
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" /> Detect network & pick niche
                </>
              )}
            </button>
          </form>

          <div className="lg:col-span-2 space-y-4">
            {lastConnected ? (
              <div data-testid="detection-result" className="border border-sky-500/40 rounded-xl p-6 bg-gradient-to-br from-sky-500/8 to-transparent">
                <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mb-2">AI Detection Result</p>
                <h3 className="font-serif text-xl text-sky-100 mb-3">{lastConnected.network.network_name}</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between border-b border-slate-800 py-1.5">
                    <span className="text-neutral-500">Method</span>
                    <span className="text-sky-100 font-mono capitalize">{lastConnected.network.detection_method}</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800 py-1.5">
                    <span className="text-neutral-500">Confidence</span>
                    <span className="text-sky-100 font-mono">{(lastConnected.network.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800 py-1.5">
                    <span className="text-neutral-500">Cookie window</span>
                    <span className="text-sky-100 font-mono">{lastConnected.network.cookie_days} days</span>
                  </div>
                  <div className="flex justify-between border-b border-slate-800 py-1.5">
                    <span className="text-neutral-500">Commission</span>
                    <span className="text-sky-100 font-mono">{lastConnected.network.commission_range}</span>
                  </div>
                </div>
                <div className="mt-5 pt-5 border-t border-slate-800">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Recommended niche</p>
                  <h4 className="font-serif text-lg text-sky-200">{lastConnected.niche.niche}</h4>
                  <p className="text-xs text-neutral-400 mt-1">{lastConnected.niche.content_angle}</p>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {lastConnected.niche.primary_keywords?.slice(0, 4).map((k) => (
                      <span key={k} className="text-[10px] font-mono px-2 py-0.5 border border-sky-500/30 rounded text-sky-300/80">{k}</span>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-slate-800 rounded-xl p-6 text-sm text-neutral-500 leading-relaxed">
                <Radio className="w-5 h-5 text-sky-400 mb-3 animate-pulse" />
                <p className="font-serif text-lg text-sky-100 mb-2">Ready to detect.</p>
                Your affiliate ID pattern is analyzed against Amazon, ClickBank, ShareASale, CJ, Impact, Rakuten & Awin signatures. GPT-5.6 handles ambiguous cases.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ACCOUNTS TAB */}
      {tab === "accounts" && (
        <div className="space-y-4">
          {accounts.length === 0 ? (
            <p className="text-neutral-500 text-center py-16">No accounts yet — connect one under tab 01.</p>
          ) : (
            accounts.map((a) => (
              <div key={a.id} data-testid={`account-row-${a.id}`} className="border border-slate-800 rounded-lg p-5 hover:border-sky-400/40 transition">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-sky-400">{a.network.network_name}</span>
                      <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border ${
                        a.status === "connected" ? "border-emerald-500/40 text-emerald-400" : "border-slate-700 text-neutral-500"
                      }`}>
                        ● {a.status}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg text-sky-100">{a.niche.niche}</h3>
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><p className="text-[10px] font-mono uppercase text-neutral-500">Aff ID</p><p className="text-sky-100 font-mono">{a.affiliate_id}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-neutral-500">Country</p><p className="text-sky-100">{a.country}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-neutral-500">Clicks</p><p className="text-sky-100 font-numeric">{a.total_clicks}</p></div>
                      <div><p className="text-[10px] font-mono uppercase text-neutral-500">Earned</p><p className="text-sky-300 font-numeric">${a.total_commission.toFixed(2)}</p></div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      data-testid={`btn-select-${a.id}`}
                      onClick={() => { setSelectedAccount(a); setTab("studio"); }}
                      className="px-3 py-1.5 text-xs bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium"
                    >
                      Generate
                    </button>
                    <button
                      data-testid={`btn-sync-${a.id}`}
                      onClick={() => openSyncModal(a)}
                      className="px-3 py-1.5 text-xs border border-sky-400 text-sky-300 hover:bg-sky-400 hover:text-slate-950 rounded flex items-center gap-1"
                    >
                      <RadioTower className="w-3 h-3" /> Real sync
                    </button>
                    <button
                      data-testid={`btn-delete-account-${a.id}`}
                      onClick={() => handleDeleteAccount(a.id)}
                      className="px-3 py-1.5 text-xs border border-slate-800 text-neutral-500 hover:text-red-400 hover:border-red-500/40 rounded flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Disconnect
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* STUDIO TAB */}
      {tab === "studio" && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <form onSubmit={handleGenerate} className="lg:col-span-3 border border-slate-800 rounded-xl p-8 bg-slate-900/30 space-y-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Step 03</p>
              <h2 className="font-serif text-2xl text-sky-100 mt-1">Generate content</h2>
              <p className="text-sm text-neutral-500 mt-2">Full articles, reviews, and comparison tables with affiliate links auto-inserted.</p>
            </div>

            {generateError && <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{generateError}</div>}

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Account</label>
              <select
                data-testid="select-account"
                value={selectedAccount?.id || ""}
                onChange={(e) => setSelectedAccount(accounts.find((a) => a.id === e.target.value))}
                className="w-full bg-slate-900 border border-slate-800 rounded py-2.5 px-3 text-sky-50 focus:border-sky-400 outline-none"
              >
                <option value="">Select an affiliate account…</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.network.network_name} — {a.niche.niche} ({a.affiliate_id})</option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Content type</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "article", label: "SEO Article", icon: FileText },
                  { key: "review", label: "Product Review", icon: Star },
                  { key: "comparison", label: "Comparison", icon: Table },
                ].map((t) => {
                  const Icon = t.icon;
                  const active = contentType === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`content-type-${t.key}`}
                      onClick={() => setContentType(t.key)}
                      className={`p-3 rounded border text-sm transition flex flex-col items-center gap-1 ${
                        active
                          ? "border-sky-400 bg-sky-400/10 text-sky-100"
                          : "border-slate-800 text-neutral-500 hover:text-sky-200"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">
                {contentType === "review" ? "Product name" : contentType === "comparison" ? "Category/topic" : "Topic"}
              </label>
              <input
                data-testid="input-topic"
                required
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={contentType === "review" ? "e.g. Anker Nano Charger" : "e.g. best ergonomic chairs 2026"}
                className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none transition"
              />
            </div>

            {contentType === "comparison" && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-2">Products (comma separated)</label>
                <input
                  data-testid="input-products"
                  value={products}
                  onChange={(e) => setProducts(e.target.value)}
                  placeholder="Herman Miller Aeron, Steelcase Leap, Autonomous ErgoChair"
                  className="w-full bg-transparent border-b border-slate-700 focus:border-sky-400 py-3 text-sky-50 outline-none transition"
                />
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer border border-slate-800 rounded p-3 hover:border-sky-400/40 transition">
              <input
                type="checkbox"
                data-testid="chk-include-hero"
                checked={includeHeroImage}
                onChange={(e) => setIncludeHeroImage(e.target.checked)}
                className="accent-sky-400 w-4 h-4"
              />
              <div className="flex-1">
                <p className="text-sm text-sky-100">Generate hero image with Gemini Nano Banana</p>
                <p className="text-[11px] text-neutral-500">Adds ~10-20s. Editorial 16:9 image auto-embedded at the top.</p>
              </div>
            </label>

            <div className="border border-slate-800 rounded p-3">
              <p className="text-sm text-sky-100 mb-2">Publish in additional languages <span className="text-neutral-500 text-xs">— optional</span></p>
              <p className="text-[11px] text-neutral-500 mb-3">One article per language. Adds ~45s per language. Leave empty to publish in English only.</p>
              <div className="flex flex-wrap gap-2">
                {["Hindi", "Marathi", "Gujarati", "Tamil", "Telugu", "Bengali", "Kannada", "Punjabi", "Malayalam", "Spanish", "French", "German", "Portuguese", "Japanese", "Mandarin", "Arabic", "Italian", "Dutch"].map((lang) => {
                  const active = languages.includes(lang);
                  return (
                    <button
                      key={lang}
                      type="button"
                      data-testid={`lang-${lang.toLowerCase()}`}
                      onClick={() => setLanguages((prev) => active ? prev.filter((l) => l !== lang) : [...prev, lang])}
                      className={`text-xs px-3 py-1.5 rounded font-mono uppercase transition ${
                        active
                          ? "bg-sky-400 text-slate-950"
                          : "border border-slate-800 text-neutral-400 hover:text-sky-200 hover:border-sky-400/50"
                      }`}
                    >
                      {lang}
                    </button>
                  );
                })}
              </div>
              {languages.length > 0 && (
                <p className="mt-3 text-[11px] text-sky-300 font-mono">
                  Will publish {languages.length + 1} versions (English + {languages.length} translated). Est. {(languages.length + 1) * 40}s.
                </p>
              )}
            </div>

            <button
              data-testid="btn-generate"
              type="submit"
              disabled={generating || !selectedAccount}
              className="w-full py-3.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium rounded-md transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  Writing, optimizing, injecting links…
                </>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate & publish</>
              )}
            </button>
          </form>

          <div className="lg:col-span-2">
            {selectedAccount ? (
              <div className="border border-slate-800 rounded-xl p-6 space-y-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Target account</p>
                <h3 className="font-serif text-lg text-sky-100">{selectedAccount.network.network_name}</h3>
                <div className="text-sm space-y-2">
                  <p className="text-neutral-400">Niche: <span className="text-sky-200">{selectedAccount.niche.niche}</span></p>
                  <p className="text-neutral-400">Audience: <span className="text-sky-100/80">{selectedAccount.niche.target_audience}</span></p>
                  <p className="text-neutral-400">Competition: <span className="font-mono text-sky-300">{selectedAccount.niche.competition_level}</span></p>
                  <p className="text-neutral-400">Volume: <span className="font-mono text-sky-300">{selectedAccount.niche.monthly_search_volume_estimate}</span></p>
                </div>
                <div className="pt-3 border-t border-slate-800">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Suggested keywords</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedAccount.niche.primary_keywords?.map((k) => (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setTopic(k)}
                        className="text-[10px] font-mono px-2 py-0.5 border border-slate-800 rounded text-sky-300/80 hover:border-sky-400"
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-slate-800 rounded-xl p-6 text-sm text-neutral-500">
                Select an account to start generating content.
              </div>
            )}
          </div>
        </div>
      )}

      {/* CONTENT TAB */}
      {tab === "content" && (
        <div className="space-y-3">
          {content.length === 0 ? (
            <p className="text-neutral-500 text-center py-16">No content yet — generate your first piece under tab 03.</p>
          ) : (
            content.map((c) => (
              <div
                key={c.id}
                data-testid={`content-row-${c.id}`}
                className="border border-slate-800 rounded-lg p-5 hover:border-sky-400/40 transition group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-sky-400">{c.content_type}</span>
                      <span className="text-[10px] font-mono text-neutral-500">SEO {c.seo_score}</span>
                    </div>
                    <h3 className="font-serif text-lg text-sky-100 group-hover:text-sky-300 transition truncate">{c.title}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-[11px] font-mono text-neutral-500">
                      <span>{c.niche}</span>
                      <span>{c.network_key}</span>
                      <span>{c.clicks} clicks</span>
                      <span>{c.conversions} conv</span>
                      <span className="text-sky-300">${c.commission.toFixed(2)}</span>
                      {c.refresh_history_count > 0 && (
                        <span className="text-emerald-400 flex items-center gap-1"><RefreshCw className="w-3 h-3" />refreshed ×{c.refresh_history_count}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button
                      data-testid={`btn-view-${c.id}`}
                      onClick={() => openContent(c.id)}
                      className="text-xs px-3 py-1.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded flex items-center gap-1"
                    >
                      Preview <ChevronRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => copyLink(c.trackable_link)}
                      className="text-xs px-3 py-1.5 border border-slate-800 hover:border-sky-400/50 text-neutral-400 rounded flex items-center gap-1"
                    >
                      <Copy className="w-3 h-3" /> Link
                    </button>
                    {c.content_type === "article" && (
                      <button
                        data-testid={`btn-refresh-${c.id}`}
                        onClick={() => handleRefreshContent(c.id)}
                        className="text-xs px-3 py-1.5 border border-slate-800 hover:border-sky-400/50 text-sky-300 rounded flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" /> AI refresh
                      </button>
                    )}
                    {c.content_type === "article" && (
                      <button
                        data-testid={`btn-translate-${c.id}`}
                        onClick={() => handleTranslateAll(c.id)}
                        className="text-xs px-3 py-1.5 border border-slate-800 hover:border-emerald-400/50 text-emerald-300 rounded flex items-center gap-1"
                      >
                        <Languages className="w-3 h-3" /> Translate all
                      </button>
                    )}
                    {c.content_type === "article" && (
                      <button
                        data-testid={`btn-public-${c.id}`}
                        onClick={() => togglePublic(c)}
                        className={`text-xs px-3 py-1.5 border rounded flex items-center gap-1 ${c.is_public ? "border-emerald-500/40 text-emerald-300" : "border-slate-800 hover:border-sky-400/50 text-neutral-400"}`}
                        title={c.is_public ? `Public at /read/${c.slug}` : "Currently private — click to publish"}
                      >
                        {c.is_public ? <><Globe className="w-3 h-3" /> Public</> : <><Lock className="w-3 h-3" /> Private</>}
                      </button>
                    )}
                    {c.is_public && c.slug && (
                      <button
                        data-testid={`btn-copy-public-${c.id}`}
                        onClick={() => copyPublicUrl(c.slug)}
                        className="text-xs px-3 py-1.5 border border-slate-800 hover:border-sky-400/50 text-sky-300 rounded flex items-center gap-1"
                      >
                        <Copy className="w-3 h-3" /> Public URL
                      </button>
                    )}
                    <button
                      data-testid={`btn-tweet-${c.id}`}
                      onClick={() => openTweetModal(c)}
                      className="text-xs px-3 py-1.5 border border-slate-800 hover:border-sky-400/50 text-sky-300 rounded flex items-center gap-1"
                    >
                      <Twitter className="w-3 h-3" /> Tweet
                    </button>
                    <button
                      data-testid={`btn-delete-content-${c.id}`}
                      onClick={() => handleDeleteContent(c.id)}
                      className="text-xs px-3 py-1.5 border border-slate-800 hover:border-red-500/40 text-neutral-500 hover:text-red-400 rounded flex items-center gap-1"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ANALYTICS TAB */}
      {tab === "analytics" && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border border-sky-400/40 rounded-lg p-5 bg-gradient-to-br from-sky-500/8 to-transparent">
              <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">Commission</p>
              <p className="font-serif text-3xl text-sky-100 mt-2 font-numeric">${analytics.total_commission.toFixed(2)}</p>
            </div>
            <div className="border border-slate-800 rounded-lg p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Clicks</p>
              <p className="font-serif text-3xl text-sky-100 mt-2 font-numeric">{analytics.total_clicks}</p>
            </div>
            <div className="border border-slate-800 rounded-lg p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Conversions</p>
              <p className="font-serif text-3xl text-sky-100 mt-2 font-numeric">{analytics.total_conversions}</p>
            </div>
            <div className="border border-slate-800 rounded-lg p-5">
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">CR %</p>
              <p className="font-serif text-3xl text-sky-100 mt-2 font-numeric">{analytics.conversion_rate}%</p>
            </div>
          </div>

          <div className="border border-slate-800 rounded-xl p-6">
            <h3 className="font-serif text-xl text-sky-100 mb-4">Top performing content</h3>
            {analytics.top_content.length === 0 ? (
              <p className="text-sm text-neutral-500">Nothing to show yet. Test-click a piece from the Content tab and watch the numbers rise.</p>
            ) : (
              <div className="divide-y divide-neutral-800">
                {analytics.top_content.map((c, i) => (
                  <div key={c.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-xs text-sky-400 w-6">#{i + 1}</span>
                      <span className="text-sky-100 truncate">{c.title}</span>
                    </div>
                    <div className="flex gap-4 font-mono text-xs text-neutral-400 flex-shrink-0">
                      <span>{c.clicks} clk</span>
                      <span>{c.conversions} conv</span>
                      <span className="text-sky-300">${c.commission.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cross-language topics — groups multilingual variants of the same article */}
          {topics && topics.total_topics > 0 && (
            <div data-testid="topics-panel" className="border border-slate-800 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-serif text-xl text-sky-100">Topics · cross-language rollup</h3>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    Multilingual variants grouped by shared topic. {topics.total_topics} topics · {topics.total_variants} variants · ${topics.total_commission.toFixed(2)} total
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                {topics.topics.slice(0, 20).map((t) => (
                  <div key={t.topic_slug} data-testid={`topic-row-${t.topic_slug}`} className="border border-slate-800 rounded-lg p-4 hover:border-sky-400/40 transition">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-sky-400">
                            {t.variant_count} variant{t.variant_count > 1 ? "s" : ""}
                          </span>
                          {t.languages.map((lang) => (
                            <span key={lang} className="text-[10px] font-mono px-2 py-0.5 border border-slate-700 rounded text-sky-300/80">
                              {lang}
                            </span>
                          ))}
                        </div>
                        <h4 className="font-serif text-base text-sky-100 truncate">{t.topic_display}</h4>
                        <div className="flex flex-wrap gap-4 mt-2 text-[11px] font-mono text-neutral-500">
                          <span>{t.total_clicks} clicks</span>
                          <span>{t.total_conversions} conv</span>
                          <span>{t.conversion_rate}% CR</span>
                          <span>SEO {t.avg_seo}</span>
                        </div>
                      </div>
                      <div className="text-right font-numeric flex-shrink-0">
                        <p className="text-lg text-sky-300">${t.total_commission.toFixed(2)}</p>
                        <p className="font-mono text-[10px] uppercase text-neutral-500">rolled up</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="border border-slate-800 rounded-xl p-6">
            <h3 className="font-serif text-xl text-sky-100 mb-4">Network breakdown</h3>
            {analytics.network_breakdown.length === 0 ? (
              <p className="text-sm text-neutral-500">Connect an account to see network stats.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analytics.network_breakdown.map((n) => (
                  <div key={n.network} className="border border-slate-800 rounded-lg p-4">
                    <div className="flex justify-between items-baseline">
                      <p className="font-serif text-lg text-sky-100">{n.network}</p>
                      <p className="font-numeric text-sky-300">${n.commission.toFixed(2)}</p>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] font-mono">
                      <div><p className="text-neutral-500">Accts</p><p className="text-sky-100">{n.accounts}</p></div>
                      <div><p className="text-neutral-500">Clicks</p><p className="text-sky-100">{n.clicks}</p></div>
                      <div><p className="text-neutral-500">Conv</p><p className="text-sky-100">{n.conversions}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* OAuth 2.0 Tweet Preview modal — shown after auto-post is queued */}
      <TweetPreviewModal
        pendingTweet={pendingTweet}
        onClose={() => setPendingTweet(null)}
        onApproved={(result) => {
          setPendingTweet(null);
          if (result?.ok) showToast(`Posted to X · ${result.url || "🎉"}`);
          else showToast(result?.error || "Post failed", "err");
        }}
        showToast={showToast}
      />

      {/* Tweet composer modal */}
      {tweetModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={() => setTweetModal(null)}>
          <div className="bg-slate-950 border border-slate-800 rounded-xl max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <Twitter className="w-5 h-5 text-sky-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">Auto-composed tweet</p>
                  <h3 className="font-serif text-lg text-sky-100 truncate">{tweetModal.content.title}</h3>
                </div>
              </div>
              <button data-testid="btn-close-tweet" onClick={() => setTweetModal(null)} className="text-neutral-400 hover:text-sky-300"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              {tweetModal.loading && (
                <div className="text-center py-10 text-neutral-500 flex items-center justify-center gap-3">
                  <div className="w-5 h-5 border-2 border-sky-400 border-t-transparent rounded-full animate-spin" />
                  Writing something scroll-stopping…
                </div>
              )}
              {tweetModal.error && (
                <div className="border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">{tweetModal.error}</div>
              )}
              {tweetModal.result && !tweetModal.loading && (
                <>
                  {/* Variant picker */}
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Choose a variant</p>
                    <div className="space-y-2">
                      {[
                        { key: "primary", label: "Primary", text: tweetModal.result.primary },
                        { key: "alt_1", label: "Alt 1", text: tweetModal.result.alt_1 },
                        { key: "alt_2", label: "Alt 2", text: tweetModal.result.alt_2 },
                      ].filter((v) => v.text).map((v) => (
                        <button
                          key={v.key}
                          data-testid={`variant-${v.key}`}
                          onClick={() => setTweetModal({ ...tweetModal, selected: v.key })}
                          className={`w-full text-left p-3 rounded border text-sm transition ${
                            tweetModal.selected === v.key
                              ? "border-sky-400 bg-sky-500/10 text-sky-50"
                              : "border-slate-800 text-neutral-300 hover:border-sky-500/40"
                          }`}
                        >
                          <span className="font-mono text-[10px] uppercase tracking-widest text-sky-400 mr-2">{v.label}</span>
                          {v.text}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Composed preview */}
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-2 flex items-center justify-between">
                      <span>Preview (with link + hashtags)</span>
                      <span className={chosenTweet().length > 280 ? "text-red-400" : "text-sky-300"}>{chosenTweet().length}/280</span>
                    </p>
                    <div data-testid="tweet-preview" className="border border-slate-800 rounded p-4 bg-slate-900 text-sky-50 leading-relaxed whitespace-pre-wrap break-words">
                      {chosenTweet()}
                    </div>
                  </div>

                  <p className="text-[11px] text-neutral-500 italic">
                    Hook style: <span className="text-sky-300 uppercase">{tweetModal.result.hook_style}</span> · Auto-post activates when you add Twitter API keys under Settings.
                  </p>

                  <div className="flex gap-2">
                    <button
                      data-testid="btn-copy-tweet"
                      onClick={copyTweet}
                      className="flex-1 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium rounded flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" /> Copy tweet
                    </button>
                    <button
                      data-testid="btn-open-x"
                      onClick={openInX}
                      className="flex-1 py-2.5 border border-sky-400 text-sky-300 hover:bg-sky-400 hover:text-slate-950 rounded flex items-center justify-center gap-2 transition"
                    >
                      <Twitter className="w-4 h-4" /> Open in X
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Network sync modal */}
      {syncModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={() => setSyncModal(null)}>
          <div className="bg-slate-950 border border-slate-800 rounded-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">Real network sync</p>
                <h3 className="font-serif text-xl text-sky-100">{syncModal.account.network.network_name}</h3>
              </div>
              <button data-testid="btn-close-sync" onClick={() => setSyncModal(null)} className="text-neutral-400 hover:text-sky-300"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSaveSync} className="p-6 space-y-4">
              <p className="text-sm text-neutral-500">
                Add real API credentials so we can pull actual click and commission data directly from{" "}
                <span className="text-sky-300">{syncModal.account.network.network_name}</span>. All fields are encrypted at rest.
              </p>

              {syncModal.creds?.configured && (
                <div className="border border-emerald-500/30 bg-emerald-500/5 rounded p-3 text-xs font-mono">
                  <p className="text-emerald-400 uppercase tracking-widest mb-1">Configured</p>
                  <p className="text-neutral-400">Updated: {syncModal.creds.updated_at}</p>
                  {syncModal.creds.masked?.access_key && <p className="text-neutral-400">Access key: {syncModal.creds.masked.access_key}</p>}
                  {syncModal.creds.masked?.api_key && <p className="text-neutral-400">API key: {syncModal.creds.masked.api_key}</p>}
                </div>
              )}

              {syncModal.account.network.network_key === "amazon" && (
                <>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-neutral-500 block mb-1">PA-API Access Key</label>
                    <input data-testid="input-access-key" value={syncForm.access_key} onChange={(e) => setSyncForm({...syncForm, access_key: e.target.value})} className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none" placeholder="AKIAXXXXXXXXXXXXXXXX" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-neutral-500 block mb-1">PA-API Secret Key</label>
                    <input data-testid="input-secret-key" type="password" value={syncForm.secret_key} onChange={(e) => setSyncForm({...syncForm, secret_key: e.target.value})} className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none" placeholder="••••••••••••••••" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-neutral-500 block mb-1">Marketplace</label>
                    <select value={syncForm.marketplace} onChange={(e) => setSyncForm({...syncForm, marketplace: e.target.value})} className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50">
                      {["US","UK","DE","JP","IN","CA","FR","IT","ES","AU","BR","MX"].map((m) => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                </>
              )}
              {syncModal.account.network.network_key === "impact" && (
                <>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-neutral-500 block mb-1">Account SID</label>
                    <input value={syncForm.account_sid} onChange={(e) => setSyncForm({...syncForm, account_sid: e.target.value})} className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none" placeholder="IRxxxxxxxxx" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase text-neutral-500 block mb-1">Auth Token</label>
                    <input type="password" value={syncForm.auth_token} onChange={(e) => setSyncForm({...syncForm, auth_token: e.target.value})} className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none" placeholder="••••••••••••••••" />
                  </div>
                </>
              )}
              {!["amazon","impact"].includes(syncModal.account.network.network_key) && (
                <div>
                  <label className="font-mono text-[10px] uppercase text-neutral-500 block mb-1">API Key</label>
                  <input type="password" value={syncForm.api_key} onChange={(e) => setSyncForm({...syncForm, api_key: e.target.value})} className="w-full bg-slate-900 border border-slate-800 rounded px-3 py-2 text-sky-50 font-mono text-sm focus:border-sky-400 outline-none" placeholder="Provided by your network dashboard" />
                </div>
              )}

              {syncResult && (
                <div className={`border rounded p-3 text-xs font-mono ${syncResult.ok ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" : "border-red-500/40 bg-red-500/5 text-red-300"}`}>
                  <p className="uppercase tracking-widest mb-1">{syncResult.ok ? "Sync live" : "Sync issue"}</p>
                  <pre className="whitespace-pre-wrap break-all">{JSON.stringify(syncResult, null, 2).slice(0, 400)}</pre>
                </div>
              )}

              <button
                data-testid="btn-save-sync"
                type="submit"
                disabled={syncSaving}
                className="w-full py-3 bg-sky-400 hover:bg-sky-300 text-slate-950 font-medium rounded flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {syncSaving ? "Saving & testing…" : (<><KeyRound className="w-4 h-4" /> Save & sync now</>)}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Content preview modal */}
      {viewContent && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={() => setViewContent(null)}>
          <div
            className="bg-slate-950 border border-slate-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-800">
              <div className="min-w-0">
                <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">{viewContent.content_type}</p>
                <h3 className="font-serif text-xl text-sky-100 truncate">{viewContent.title}</h3>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={viewContent.trackable_link.startsWith("http") ? viewContent.trackable_link : `${process.env.REACT_APP_BACKEND_URL}${viewContent.trackable_link}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="btn-test-click"
                  className="text-xs px-3 py-1.5 border border-sky-400 text-sky-300 hover:bg-sky-400 hover:text-slate-950 rounded flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Test click
                </a>
                <button onClick={() => setViewContent(null)} data-testid="btn-close-modal" className="text-neutral-400 hover:text-sky-300">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto p-8 flex-1">
              {viewContent.hero_image_url && (
                <img
                  src={viewContent.hero_image_url.startsWith("http") ? viewContent.hero_image_url : `${process.env.REACT_APP_BACKEND_URL}${viewContent.hero_image_url}`}
                  alt={viewContent.title}
                  className="w-full rounded-lg mb-6 border border-slate-800"
                />
              )}
              <div className="article-html" dangerouslySetInnerHTML={{ __html: viewContent.html }} />
            </div>
            <div className="border-t border-slate-800 p-4 flex items-center justify-between text-xs font-mono">
              <span className="text-neutral-500">Affiliate: <span className="text-sky-300 break-all">{viewContent.affiliate_link}</span></span>
              <div className="flex gap-4 text-neutral-400">
                <span>SEO {viewContent.seo_score}</span>
                <span>{viewContent.clicks} clicks</span>
                <span className="text-sky-300">${viewContent.commission?.toFixed(2) ?? 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
